import {
  nomenclatorService,
  SamedayCity,
  SamedayCounty,
} from "@/services/NomeclatorService";
import { useCallback, useEffect, useState } from "react";

interface UseNomenclatorReturn {
  counties: SamedayCounty[];
  cities: Record<number, SamedayCity[]>;
  isLoading: boolean;
  error: string | null;
  preloadNomenclator: () => Promise<void>;
  getCitiesForCounty: (countyId: number) => Promise<void>;
}

export const useNomenclator = (): UseNomenclatorReturn => {
  const [counties, setCounties] = useState<SamedayCounty[]>([]);
  const [cities, setCities] = useState<Record<number, SamedayCity[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preloadNomenclator = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Log environment configuration for debugging
      console.log("Environment check:", {
        NODE_ENV: process.env.NODE_ENV,
        VITE_API_URL: (import.meta as any).env?.VITE_API_URL,
        hasApiUrl: !!(import.meta as any).env?.VITE_API_URL,
      });

      const countiesData: SamedayCounty[] =
        await nomenclatorService.getCounties();
      setCounties(countiesData);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";

      // Enhanced error message with debugging info
      let enhancedError = errorMessage;
      if (
        errorMessage.includes("text/html") ||
        errorMessage.includes("ngrok")
      ) {
        enhancedError = `Backend API Error: Backend server not reachable or wrong API base URL.

📋 Please check:
1. Is your backend server running?
2. Is VITE_API_URL set correctly in your .env (e.g., http://localhost:3001)?
3. If using ngrok, set VITE_API_URL to your ngrok URL.

Original error: ${errorMessage}`;
      }

      setError(enhancedError);
      console.error("Error preloading nomenclator:", err);
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed

  const getCitiesForCounty = useCallback(async (countyId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const citiesData: SamedayCity[] =
        await nomenclatorService.getCitiesForCounty(countyId);

      setCities((prev) => ({
        ...prev,
        [countyId]: citiesData,
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
      console.error("Error fetching cities:", err);
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed

  // Load counties on mount only
  useEffect(() => {
    preloadNomenclator();
  }, []); // Run once on mount

  return {
    counties,
    cities,
    isLoading,
    error,
    preloadNomenclator,
    getCitiesForCounty,
  };
};
